<template>
    <div class="-m-5 md:m-0 h-full flex-1 overflow-hidden">
        <tabulator
            :columns="columns"
            :actions="actions"
            :refresh.sync="refreshTable"
            :loading="tableIsLoading"   
            ajax-path="pbxware/ivrs"
            :ajax-params="{tenant_id: $route.meta.serverId}"
        />
        <app-modal
            :show.sync="showEditModal"
            width="md:w-1/2 lg:w-1/4"
            @close="editModalClosed"
        >
            <template slot="header">Edit IVR</template>
            <template slot="body">                
                <app-input
                    v-model="editForm.name"
                    placeholder="Name"
                    :error="editForm.errors.get('name')"
                    @updated="editForm.errors.clear('name')"
                    focus
                    required
                />
                <app-input
                    v-model="editForm.ext"
                    placeholder="Number"
                    :error="editForm.errors.get('ext')"
                    @updated="editForm.errors.clear('ext')"
                    required
                    integer
                />
                <app-input
                    v-model="editForm.greeting"
                    placeholder="Greeting"
                    :error="editForm.errors.get('greeting')"
                    @updated="editForm.errors.clear('greeting')"
                    required
                    :help-text="'All greetings must begin with greeting-'"
                />
                <div class="mt-5 flex items-center justify-end">
                    <app-button 
                        class="btn-white" 
                        @click="showEditModal = false"
                        :disable="editFormIsLoading"
                    >Cancel</app-button>
                    <app-button 
                        class="btn-blue ml-3" 
                        @click="update"
                        :loading="editFormIsLoading"
                    >Update</app-button>
                </div>
            </template>
        </app-modal>
        <app-modal
            :show.sync="showAddModal"
            width="md:w-1/2 lg:w-1/4"
            @close="addModalClosed"
        >
            <template slot="header">Add IVR</template>
            <template slot="body">
                <app-input
                    v-model="addForm.name"
                    placeholder="Name"
                    :error="addForm.errors.get('name')"
                    @updated="addForm.errors.clear('name')"
                    focus
                    required
                />
                <app-input
                    v-model="addForm.ext"
                    placeholder="Number"
                    :error="addForm.errors.get('ext')"
                    @updated="addForm.errors.clear('ext')"
                    required
                    integer
                />
                <app-input
                    v-model="addForm.greeting"
                    placeholder="Greeting"
                    :error="addForm.errors.get('greeting')"
                    @updated="addForm.errors.clear('greeting')"
                    required
                    :help-text="'All greetings must begin with greeting-'"
                />
                <div class="mt-5 flex items-center justify-end">
                    <app-button 
                        class="btn-white" 
                        @click="showAddModal = false"
                        :disable="addFormIsLoading"
                    >Cancel</app-button>
                    <app-button 
                        class="btn-blue ml-3" 
                        @click="store"
                        :loading="addFormIsLoading"
                    >Add</app-button>
                </div>
            </template>
        </app-modal>
    </div>
</template>

<script>
    import { mapGetters } from 'vuex';
    import Form from '@/helpers/Form';
    import Http from '@/helpers/Http';

    export default {
        data() {
            return {
                addForm: new Form({
                    name: null,
                    ext: null,
                    greeting: 'greeting-',
                }),
                editForm: new Form({
                    server: null,
                    name: null,
                    ext: null,
                    greeting: 'greeting-',
                }),
                selected: {},
                editFormIsLoading: false,
                addFormIsLoading: false,
                tableIsLoading: false,
                refreshTable: false,
                showEditModal: false,
                showAddModal: false,
                q: null,
                generalInputIsLoading: false,
                columns: [
                    {
                        title: 'Name',
                        field: 'name',
                        sorter: 'string',
                        minWidth: 150,
                    },
                    {
                        title: 'Number',
                        field: 'ext',
                        sorter: 'string',
                        minWidth: 150,
                    },
                    {
                        title: 'Type',
                        field: 'type_for_human',
                        sorter: 'string',
                        minWidth: 150,
                    },
                    {
                        title: 'Status',
                        field: 'status',
                        sorter: 'string',
                        minWidth: 150,
                    }
                ],
                actions: [
                    {
                        label: 'Edit',
                        icon: () => import('@/icons/EditOutlined.vue'),
                        handler: ivrs => {
                            this.selected = ivrs[0];
                            this.editForm.set(ivrs[0]);
                            this.editForm.set({server: this.$route.meta.serverId});
                            this.showEditModal = true;
                        },
                    },
                    {
                        label: 'Delete',
                        icon: () => import('@/icons/DeleteOutlined.vue'),
                        requiredSelect: true,
                        handler: ivrs => {
                            this.deleteItem(ivrs[0]);
                        }
                    },
                    {
                        label: 'Add',
                        icon: () => import('@/icons/Add.vue'),
                        handler: this.add,
                    }
                ],
            }
        },
        methods: {
            editModalClosed() {
                this.editForm.reset();
                this.editForm.errors.clear();                
            },
            update() {
                this.editFormIsLoading = true;
                Http.patch(`pbxware/ivrs/${this.selected.id}`, this.editForm.payload())
                    .then(() => {
                        this.editFormIsLoading = false;
                        this.showEditModal = false;
                        this.refreshTable = true;
                        this.$swal.fire({
                            type: 'success',
                            title: 'Success!',
                            text: 'IVR was successfully updated.'
                        })
                    })
                    .catch(({ response }) => {
                        this.editFormIsLoading = false;
                        this.editForm.errors.record(response.data);
                    });
            },
            addModalClosed() {
                this.addForm.reset();
                this.addForm.errors.clear();
            },
            add() {
                this.showAddModal = true;
            },
            store() {
                this.addFormIsLoading = true;
                this.$store.dispatch('pbxware/ivr/store', this.addForm.payload())
                    .then(() => {
                        this.addFormIsLoading = false;
                        this.showAddModal = false;
                        this.refreshTable = true;
                        this.$swal.fire({
                            type: 'success',
                            title: 'Success!',
                            text: 'IVR was added successfully.'
                        })
                    })
                    .catch(({ response }) => {
                        this.addFormIsLoading = false;
                        this.addForm.errors.record(response.data);
                    })
            },
            deleteItem(item) {
                this.$swal.fire({
                    type: 'warning',
                    title: 'Are you sure?',
                    html: `You are about to delete "<strong>${item.name}</strong>" IVR.`,
                    showCancelButton: true,
                    confirmButtonColor: '#f56565',
                    confirmButtonText: 'Yes, delete it!',
                    showLoaderOnConfirm: true,
                    preConfirm: (response) => {
                        return this.$store.dispatch('pbxware/ivr/delete', item.id);
                    },
                    allowOutsideClick: () => !this.$swal.isLoading()
                })
                .then((result) => {
                    if (result.value) {
                        this.refreshTable = true;
                        this.$swal.fire({
                            type: 'success',
                            title: 'Success!',
                            text: 'IVR was successfully deleted.'
                        })
                    }
                })
                
            },
        }
    }
</script>